import React, { useState } from 'react';
import { Form, TextArea } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import CustomModal from 'src/components/configuracion/components/customModal';
import ScfoButton from 'src/components/shared/ScfoButton';
import {
  CreateDocumentReport,
  ReportReason,
  ReportReasonLabels,
} from 'src/graphql/documentReports';
import { importAlertState } from 'src/redux/actions/alert';
import { dt } from 'src/utils/documentsI18n';
import 'src/components/documentostributarios/ModalDeleteObligations.scss';
import './ReportDocumentModal.scss';

const ReportDocumentModal = ({
  open,
  onClose,
  documentId,
  documentType,
  onReportSuccess,
  hasMatches = false,
  hasAsientos = false,
}) => {
  const dispatch = useDispatch();
  const [selectedReason, setSelectedReason] = useState(null);
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { selectedCompany } = useSelector((state) => state.companyList);
  const { executeMutation: createReport } = CreateDocumentReport();

  const isBlocked = hasMatches || hasAsientos;
  const blockedTitle = hasMatches && hasAsientos
    ? dt('report.blockedTitleBoth')
    : hasAsientos
      ? dt('report.blockedTitleAsientos')
      : dt('report.blockedTitle');
  const blockedBody = hasMatches && hasAsientos
    ? dt('report.blockedBodyBoth')
    : hasAsientos
      ? dt('report.blockedBodyAsientos')
      : dt('report.blockedBody');

  const reasons = [
    { key: ReportReason.DUPLICATED, label: ReportReasonLabels[ReportReason.DUPLICATED] },
    { key: ReportReason.INCORRECT_AMOUNT, label: ReportReasonLabels[ReportReason.INCORRECT_AMOUNT] },
    { key: ReportReason.INCORRECT_DATE, label: ReportReasonLabels[ReportReason.INCORRECT_DATE] },
    { key: ReportReason.NOT_RECOGNIZED, label: ReportReasonLabels[ReportReason.NOT_RECOGNIZED] },
    { key: ReportReason.OTHER, label: ReportReasonLabels[ReportReason.OTHER] },
  ];

  const resetForm = () => {
    setSelectedReason(null);
    setDetails('');
    setError(null);
  };

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedReason) {
      setError(dt('report.selectReason'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createReport({
        input: {
          companyId: selectedCompany?.id,
          documentId,
          documentType,
          reason: selectedReason,
          details: details.trim() || undefined,
        },
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      dispatch(
        importAlertState({
          type: 'success',
          isOpen: true,
          message: dt('report.success'),
        })
      );

      if (onReportSuccess) {
        onReportSuccess(result.data.createDocumentReport);
      }

      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || dt('report.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      title={dt('report.title')}
      size="small"
      dimmer="blurring"
      classNameModal="delete-obligation-modal report-document-modal"
      classNameContent="delete-obligation-modal-content"
      closeOnDimmerClick={!loading}
      closeOnEscape={!loading}
      customActions={
        <div className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-w-full">
          <ScfoButton type="button" variant="ghost" onClick={handleClose} disabled={loading}>
            {dt('common.cancel')}
          </ScfoButton>
          {!isBlocked && (
            <ScfoButton
              type="button"
              variant="danger"
              onClick={handleSubmit}
              disabled={!selectedReason || loading}
              loading={loading}
            >
              {dt('report.report')}
            </ScfoButton>
          )}
        </div>
      }
      content={
        <div className="delete-obligation-modal-body">
          <div className="delete-obligation-modal-icon" aria-hidden>
            <FeatherIcon icon="flag" size={22} />
          </div>

          {isBlocked ? (
            <>
              <p className="delete-obligation-modal-lead">
                <strong>{blockedTitle}</strong>
              </p>
              <p className="delete-obligation-modal-warning">{blockedBody}</p>
            </>
          ) : (
            <>
              <p className="delete-obligation-modal-lead">
                {dt('report.selectReason')}
              </p>

              <div className="delete-obligation-modal-summary report-reasons-compact">
                {reasons.map((reason) => (
                  <button
                    key={reason.key}
                    type="button"
                    className={`report-reason-chip ${selectedReason === reason.key ? 'selected' : ''}`}
                    onClick={() => setSelectedReason(reason.key)}
                  >
                    <span className={`report-radio ${selectedReason === reason.key ? 'checked' : ''}`}>
                      {selectedReason === reason.key ? <span className="radio-inner" /> : null}
                    </span>
                    <span>{reason.label}</span>
                  </button>
                ))}
              </div>

              <Form className="report-details-form tw-w-full">
                <TextArea
                  placeholder={dt('report.detailsPlaceholder')}
                  value={details}
                  onChange={(e, { value }) => setDetails(value)}
                  rows={3}
                  className="report-details-textarea"
                />
              </Form>

              {error ? (
                <p className="delete-obligation-modal-warning">{error}</p>
              ) : null}
            </>
          )}
        </div>
      }
    />
  );
};

export default ReportDocumentModal;
